/// Port of mobile/lib/game/sky/cloud.dart.

import { Component } from '../engine/component';
import type { Rng } from '../engine/random';
import { defaultRng } from '../engine/random';
import { SpriteComponent, sprite } from '../engine/sprite';
import type { Vec2 } from '../engine/vector';
import { worldWidth } from '../world';
import { cloudAssets } from './skyAssets';

const cloudDim: Vec2 = { x: 46, y: 13 };

interface AmbientDeps {
  allowsAmbientMotion: () => boolean;
  currentSpeed: () => number;
  rng?: Rng;
}

/// Slow-drifting day sky cloud.
export class Cloud extends SpriteComponent {
  /// Fraction of the game speed used for horizontal drift.
  readonly driftFactor: number;

  private readonly deps: AmbientDeps;

  constructor(
    options: { position: Vec2; assetPath: string; driftFactor: number },
    deps: AmbientDeps,
  ) {
    super({
      position: options.position,
      size: { ...cloudDim },
      priority: -1,
      sprite: sprite(options.assetPath, cloudDim),
    });
    this.deps = deps;
    this.driftFactor = options.driftFactor;
  }

  override update(dt: number): void {
    if (!this.deps.allowsAmbientMotion()) return;
    this.x -= this.deps.currentSpeed() * this.driftFactor * dt;
  }
}

/// Spawns clouds one at a time with random gaps, never in batches.
export class CloudManager extends Component {
  static readonly maxCount = 3;
  static readonly minDriftFactor = 0.12;
  static readonly maxDriftFactor = 0.22;
  static readonly skyTop = 8.0;
  static readonly skyBottom = 70.0;

  /// Horizontal clearance before another cloud may enter from the right.
  static readonly minGap = 160.0;

  /// Seconds between spawn attempts (randomized each time).
  static readonly minSpawnDelay = 1.2;
  static readonly maxSpawnDelay = 3.5;

  private readonly rng: Rng;
  private readonly deps: AmbientDeps;
  private spawnCooldown = 0;

  constructor(deps: AmbientDeps) {
    super(-1);
    this.deps = deps;
    this.rng = deps.rng ?? defaultRng;

    // Always start with one cloud already on screen.
    this.add(this.spawnCloud(80 + this.rng.nextDouble() * (worldWidth * 0.45)));
    this.armCooldown(true);
  }

  override update(dt: number): void {
    if (!this.deps.allowsAmbientMotion()) return;

    for (const cloud of this.childrenOfType(Cloud)) {
      if (cloud.x + cloud.width < 0) {
        cloud.removeFromParent();
      }
    }

    this.spawnCooldown -= dt;
    if (this.spawnCooldown > 0) return;

    const clouds = this.childrenOfType(Cloud);
    if (clouds.length >= CloudManager.maxCount) {
      this.armCooldown();
      return;
    }

    const rightmost = clouds.reduce(
      (best, cloud) => Math.max(best, cloud.x),
      Number.NEGATIVE_INFINITY,
    );
    if (clouds.length > 0 && rightmost > worldWidth - CloudManager.minGap) {
      // Wait until the pack has room - short retry, not a full delay.
      this.spawnCooldown = 0.4 + this.rng.nextDouble() * 0.6;
      return;
    }

    this.add(this.spawnCloud(worldWidth + this.rng.nextDouble() * 40));
    this.armCooldown();
  }

  private armCooldown(short = false): void {
    if (short) {
      this.spawnCooldown = 0.4 + this.rng.nextDouble() * 0.8;
      return;
    }
    this.spawnCooldown =
      CloudManager.minSpawnDelay +
      this.rng.nextDouble() * (CloudManager.maxSpawnDelay - CloudManager.minSpawnDelay);
  }

  private spawnCloud(x: number): Cloud {
    const assetPath = cloudAssets[this.rng.nextInt(cloudAssets.length)];
    const y =
      CloudManager.skyTop + this.rng.nextDouble() * (CloudManager.skyBottom - CloudManager.skyTop);
    const driftFactor =
      CloudManager.minDriftFactor +
      this.rng.nextDouble() * (CloudManager.maxDriftFactor - CloudManager.minDriftFactor);

    return new Cloud({ position: { x, y }, assetPath, driftFactor }, this.deps);
  }

  reset(): void {
    this.removeWhere((child) => child instanceof Cloud);
    this.add(this.spawnCloud(80 + this.rng.nextDouble() * (worldWidth * 0.45)));
    this.armCooldown(true);
  }
}
